import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { INFORMERA_API_URL, INFORMERA_API_CLIENT_ID } from '@/lib/env';

export type InformeraLocation = {
  location: string;
  country: string;
};

export type InformeraLocationsArgs = {
  country?: string;
};

type InformeraLocalizedEntity = {
  language?: string;
  data?: {
    displayNameAlternate?: string;
  };
};

type InformeraNode = {
  id: string;
  country: string;
  localized?: InformeraLocalizedEntity[];
};

type StoresResponse = {
  data?: {
    stores?: {
      nodes?: InformeraNode[];
    };
  };
};

type AddonsResponse = {
  data?: {
    addons?: {
      nodes?: InformeraNode[];
    };
  };
};

const INFORMERA_PAGINATION_LIMIT = 9999;
const INFORMERA_DOMAIN = 'www.ikea.com';

const STORES_QUERY = `query GetUnfilteredStores($domain: String!, $country: String, $options: PaginationArgs) {
  stores(domain: $domain, country: $country, options: $options) {
    nodes {
      id
      country
      localized {
        language
        data { displayNameAlternate }
      }
    }
  }
}`;

const ADDONS_QUERY = `query GetUnfilteredAddons($domain: String!, $country: String, $options: PaginationArgs) {
  addons(domain: $domain, country: $country, options: $options) {
    nodes {
      id
      country
      localized {
        ... on LocalizedAddonWrapper {
          language
          data { displayNameAlternate }
        }
        ... on LocalizedStoreAddonWrapper {
          language
          data { displayNameAlternate }
        }
      }
    }
  }
}`;

function normalizeNodes(nodes: InformeraNode[]): InformeraLocation[] {
  const seen = new Set<string>();
  return nodes
    .map((entity): InformeraLocation | null => {
      const localizedEn = entity.localized?.find(
        (loc) =>
          loc.language?.toLowerCase() === 'en' ||
          loc.language?.toLowerCase().endsWith('/en')
      );
      const preferred = localizedEn ?? entity.localized?.[0];
      const locationName = preferred?.data?.displayNameAlternate;
      if (!locationName) return null;
      return { country: entity.country.toLowerCase(), location: locationName };
    })
    .filter((loc): loc is InformeraLocation => {
      if (!loc) return false;
      const key = `${loc.country}/${loc.location}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export const informeraApi = createApi({
  reducerPath: 'informeraApi',
  baseQuery: fetchBaseQuery({
    baseUrl: INFORMERA_API_URL,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      headers.set('x-client-id', INFORMERA_API_CLIENT_ID);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getLocations: builder.query<
      InformeraLocation[],
      InformeraLocationsArgs | void
    >({
      queryFn: async (arg, _api, _opts, baseQuery) => {
        const variables = JSON.stringify({
          domain: INFORMERA_DOMAIN,
          ...(arg?.country ? { country: arg.country } : {}),
          options: { limit: INFORMERA_PAGINATION_LIMIT },
        });
        const [storesRes, addonsRes] = await Promise.all([
          baseQuery({
            url: `?query=${encodeURIComponent(
              STORES_QUERY
            )}&variables=${encodeURIComponent(variables)}`,
          }),
          baseQuery({
            url: `?query=${encodeURIComponent(
              ADDONS_QUERY
            )}&variables=${encodeURIComponent(variables)}`,
          }),
        ]);
        if (storesRes.error) return { error: storesRes.error };
        if (addonsRes.error) return { error: addonsRes.error };
        const storeNodes =
          (storesRes.data as StoresResponse).data?.stores?.nodes ?? [];
        const addonNodes =
          (addonsRes.data as AddonsResponse).data?.addons?.nodes ?? [];
        return { data: normalizeNodes([...storeNodes, ...addonNodes]) };
      },
    }),
  }),
});

export const { useGetLocationsQuery } = informeraApi;
